import React, { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';


export const DEFAULT_DEDUPE_FIELD = {
  id: 'customer_email',
  label: 'Email',
  type: 'email',
  required: true,
  requestKey: 'email',
  dedupe: true,
  maxLength: 254,
};

const makeField = (index) => ({
  id: `field_${index}_${Date.now()}`,
  label: '',
  type: 'text',
  required: false,
  requestKey: `field_${index}`,
  dedupe: false,
  maxLength: 255,
});

const QUESTION_TEMPLATES = [
  { id: 'full_name', label: 'Full name', type: 'text', required: true, requestKey: 'full_name', dedupe: false, maxLength: 160 },
  { id: 'phone', label: 'Phone number', type: 'phone', required: false, requestKey: 'phone', dedupe: false, maxLength: 32 },
  { id: 'address', label: 'Address', type: 'text', required: false, requestKey: 'address', dedupe: false, maxLength: 500 },
  { id: 'customer_consent', label: 'I agree to share my details', type: 'consent', required: true, requestKey: 'consent', dedupe: false, maxLength: 255, consentVersion: '' },
];

const SENSITIVE_FIELD = /password|passcode|one[\s_-]?time|otp|(?:^|[\s_-])(?:pin|cvv)(?:$|[\s_-])|secret|card[\s_-]?(?:number|details)?/i;

export const validateRegistrationFields = (fields) => {
  if (!Array.isArray(fields) || fields.length === 0) return 'Add at least one customer question.';
  if (fields.some((field) => SENSITIVE_FIELD.test(`${field.id} ${field.label} ${field.requestKey}`))) {
    return 'Never ask an Agent to collect passwords, one-time codes, PINs, card details, or secrets.';
  }
  if (fields.some((field) => !field.label?.trim())) return 'Every field needs a label.';
  if (new Set(fields.map((field) => field.id)).size !== fields.length) return 'Each question needs a unique field ID.';
  if (fields.some((field) => field.requestKey === 'external_customer_ref')) return 'The customer reference must come from your registration system, not an Agent question.';
  if (!fields.some((field) => field.dedupe)) return 'Choose at least one field for duplicate detection.';
  if (fields.some((field) => !/^[a-z][a-z0-9_]{0,63}$/.test(field.id))) return 'Field IDs must start with a lowercase letter and contain only letters, numbers, or underscores.';
  if (fields.some((field) => !field.requestKey?.trim() || !Number.isInteger(field.maxLength) || field.maxLength < 1 || field.maxLength > 2000)) {
    return 'Every field needs a request key and a maximum length from 1 to 2000.';
  }
  if (fields.some((field) => field.type === 'select' && !field.options?.length)) return 'Every select field needs at least one option.';
  if (fields.some((field) => field.type === 'consent' && !field.consentVersion?.trim())) return 'Every consent field needs versioned consent copy.';
  if (fields.some((field) => field.type === 'hidden' && field.staticValue === undefined)) return 'Every hidden field needs a static value.';
  return null;
};

const RegistrationFormBuilder = ({ initialFields, onSave, onDraftChange, saving = false, draftMode = false }) => {
  const seed = useMemo(
    () => (initialFields?.length ? initialFields : [DEFAULT_DEDUPE_FIELD]),
    [initialFields],
  );
  const [fields, setFields] = useState(seed);
  const [error, setError] = useState('');

  const commit = (next) => {
    const sensitive = next.some((field) => SENSITIVE_FIELD.test(`${field.id} ${field.label} ${field.requestKey}`));
    if (sensitive) {
      setError('Never ask an Agent to collect passwords, one-time codes, PINs, card details, or secrets.');
      return;
    }
    setError('');
    setFields(next);
    onDraftChange?.(next);
  };

  const updateField = (index, patch) => {
    commit(fields.map((field, i) => (i === index ? { ...field, ...patch } : field)));
  };

  const removeField = (index) => {
    commit(fields.filter((_, i) => i !== index));
  };

  const save = () => {
    const normalized = fields.map((field) => ({
      ...field,
      label: field.label.trim(),
      requestKey: (field.requestKey || field.id).trim(),
    }));
    const validationError = validateRegistrationFields(normalized);
    if (validationError) { setError(validationError); return; }
    setError('');
    onSave(normalized);
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-4">
      <div className="rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm text-gray-700">
        Choose the customer questions your Agent will ask. Your system creates the account; the founder response supplies a safe identifier. Passwords stay on your site; Agents must never collect them or one-time codes.
      </div>
      <div className="flex flex-wrap gap-2" aria-label="Common customer questions">
        {QUESTION_TEMPLATES.map((template) => (
          <Button key={template.id} type="button" variant="outline" size="sm" disabled={fields.some((field) => field.id === template.id)} onClick={() => commit([...fields, { ...template }])}>
            <Plus className="mr-1 h-3 w-3" /> Add {template.label === 'I agree to share my details' ? 'consent' : template.label.toLowerCase()}
          </Button>
        ))}
      </div>

      {fields.map((field, index) => (
        <div key={field.id} className="grid gap-3 rounded-xl border border-gray-200 p-3 sm:grid-cols-2">
          <div>
            <Label htmlFor={`field-label-${index}`}>Field {index + 1} label</Label>
            <Input
              id={`field-label-${index}`}
              aria-label={`Field ${index + 1} label`}
              value={field.label}
              onChange={(event) => updateField(index, { label: event.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor={`field-type-${index}`}>Type</Label>
            <select
              id={`field-type-${index}`}
              value={field.type}
              onChange={(event) => updateField(index, {
                type: event.target.value,
                dedupe: ['email', 'phone', 'text'].includes(event.target.value) ? field.dedupe : false,
                required: event.target.value === 'hidden' ? false : field.required,
              })}
              className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              <option value="text">Text</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="date">Date</option>
              <option value="select">Select</option>
              <option value="consent">Consent</option>
              <option value="hidden">Hidden/static</option>
            </select>
          </div>
          <details className="sm:col-span-2 rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
            <summary className="cursor-pointer font-medium">For your developer: field mapping</summary>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor={`field-key-${index}`}>Founder request key</Label>
                <Input
                  id={`field-key-${index}`}
                  value={field.requestKey || ''}
                  onChange={(event) => updateField(index, { requestKey: event.target.value })}
                  placeholder="e.g. customer.email"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor={`field-max-${index}`}>Maximum length</Label>
                <Input
                  id={`field-max-${index}`}
                  type="number"
                  min="1"
                  max="2000"
                  value={field.maxLength || 255}
                  onChange={(event) => updateField(index, { maxLength: Number(event.target.value) })}
                  className="mt-1"
                />
              </div>
            </div>
          </details>
          {field.type === 'select' && (
            <div className="sm:col-span-2">
              <Label htmlFor={`field-options-${index}`}>Options (one per line)</Label>
              <textarea
                id={`field-options-${index}`}
                value={(field.options || []).join('\n')}
                onChange={(event) => updateField(index, {
                  options: event.target.value.split('\n').map((item) => item.trim()).filter(Boolean),
                })}
                className="mt-1 min-h-20 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
          )}
          {field.type === 'consent' && (
            <div className="sm:col-span-2">
              <Label htmlFor={`field-consent-${index}`}>Versioned consent copy</Label>
              <Input
                id={`field-consent-${index}`}
                value={field.consentVersion || ''}
                onChange={(event) => updateField(index, { consentVersion: event.target.value })}
                placeholder="e.g. marketing-consent-v1: I agree…"
                className="mt-1"
              />
            </div>
          )}
          {field.type === 'hidden' && (
            <div className="sm:col-span-2">
              <Label htmlFor={`field-static-${index}`}>Static value sent to founder</Label>
              <Input
                id={`field-static-${index}`}
                value={field.staticValue || ''}
                onChange={(event) => updateField(index, { staticValue: event.target.value })}
                className="mt-1"
              />
            </div>
          )}
          <div className="flex flex-wrap items-end gap-4 sm:col-span-2">
            <label className="flex h-9 items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={Boolean(field.required)}
                disabled={field.type === 'hidden'}
                onChange={(event) => updateField(index, { required: event.target.checked })}
              />
              Required
            </label>
            <label className="flex h-9 items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={Boolean(field.dedupe)}
                disabled={!['email', 'phone', 'text'].includes(field.type)}
                onChange={(event) => updateField(index, { dedupe: event.target.checked })}
              />
              Use for duplicate detection
            </label>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Remove field ${index + 1}`}
              disabled={fields.length === 1}
              onClick={() => removeField(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}

      {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
      <aside className="h-fit rounded-2xl border border-gray-200 bg-gray-50 p-4 shadow-theme-xs xl:sticky xl:top-24" aria-label="Agent form preview">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#1070f1]">Live Agent preview</p>
        <strong>What your Agent sees</strong>
        <p className="mt-1 text-xs text-gray-500">These are the approved questions on the campaign page.</p>
        <div className="mt-3 space-y-2">
          {fields.filter((field) => field.type !== 'hidden').map((field) => (
            <div key={field.id} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
              {field.label || 'New question'}{field.required ? ' *' : ''}
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-gray-500">Your system completes signup and later confirms the subscription.</p>
      </aside>
      <details className="rounded-xl border border-gray-200 p-3 text-xs text-gray-600 xl:col-span-2">
        <summary className="cursor-pointer font-medium">For your developer: request preview</summary>
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(
          Object.fromEntries(fields.map((field) => [
            field.requestKey || field.id,
            field.type === 'hidden' ? field.staticValue || '' : `<${field.type}>`,
          ])),
          null,
          2,
        )}</pre>
      </details>
      <div className="flex flex-wrap gap-2 xl:col-span-2">
        <Button type="button" variant="outline" onClick={() => commit([...fields, makeField(fields.length + 1)])}>
          <Plus className="mr-2 h-4 w-4" /> Add field
        </Button>
        {draftMode && <Button type="button" onClick={() => { const issue = validateRegistrationFields(fields); if (issue) setError(issue); else onDraftChange?.(fields); }}>Use these questions</Button>}
        {!draftMode && <Button type="button" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save registration form'}</Button>}
      </div>
    </div>
  );
};

export default RegistrationFormBuilder;
